// import firestore from '../../store/firestore'
import firebase from 'firebase/app'
import 'firebase/storage'
import store from '../../store'
import router from '../../router'
import marked from 'marked'
import _ from 'lodash'
import uuid from 'uuid'
import { addScriptWithSrc } from './helpers'
import { findNoteHelper, allNoteHelpers } from '../noteTemplates/noteHelpers'

class ComponentHelper {
  constructor () {
    // this._firestore = firestore
    // this._firestore_storage = firestore.storage
    this.router = router
    this.addScriptWithSrc = addScriptWithSrc
    this._storageURLs = []
    this.vue = null
  }

  _store () {
    return store
  }

  clearlog () {
    store.commit('clearComponentLog')
  }

  log (text) {
    store.commit('addToComponentLog', text)
  }

  /**
   * Returns all items/notes in the user database.
   * @public
   * @name allItems
   * @ternType fn() -> [{data: string, userData: {}}]
   * @ternDoc Returns all items/notes in the user database.
   */
  allItems () {
    return store.state.items.items
  }

  /**
   * Gets the current user session as an object describing it.
   * @returns {*|{}}
   */
  userSession () {
    // const sessionIndex = parseInt(sessionStorage.getItem('userSessionIndex') || 0)
    // return store.state.auth.currentUser.sessions[sessionIndex] || {}
    return store.getters.currentSession
  }

  /**
   * Gets the root note of this session
   * @returns {*}
   */
  sessionRootNote () {
    return findNoteHelper(this.userSession().rootNoteId)
  }

  /**
   * Returns all notes helpers in the user database.
   * @public
   * @name allNoteHelpers
   * @ternType fn() -> [{NoteHelper}]
   * @ternDoc Returns all note helpers in the user database.
   */
  allNoteHelpers () {
    return allNoteHelpers()
  }

  /**
   * Returns an item/note from the database with a specific id.
   * @public
   * @name noteHelperById
   * @ternType fn(noteId: string) -> {NoteHelper}
   * @ternDoc Returns a note helper for the note with the specified id.
   */
  noteHelperById (noteId) {
    return findNoteHelper(noteId)
  }

  /**
   * Returns the first item where item.userData[filedName] exists and item.userData[filedName] === fieldData.
   * @param fieldName string  the name of the field in userData to find
   * @param fieldData string  the contents expected in item.userData[fieldName]
   * @public
   * @name findItemWithField
   * @ternType fn(fieldName: string, fieldData: {}) -> {id: string, data: string, userData: {}}
   * @ternDoc Returns the first item where item.userData[filedName] exists and item.userData[filedName] === fieldData.
   */
  findItemWithField (fieldName, fieldData) {
    let key = _.findKey(this.allItems(), o => (fieldName in o.userData) && _.isEqual(o.userData[fieldName], fieldData))
    if (!key) return null
    return this.allItems()[key]
  }

  /**
   * Returns an item/note from the database with a specific id.
   * @public
   * @name itemWithId
   * @ternType fn(id: string) -> {data: string, userData: {}}
   * @ternDoc Returns an item/note from the database with a specific id.
   */
  itemWithId (id) {
    if (!(id in this.allItems())) return null
    return this.allItems()[id]
  }

  /**
   * Finds the name of an item/note.  It is found from the title (# tag) at the start of the item.  If the item has no title 'Untitled' is returned.
   * @param item
   * @public
   * @name getItemName
   * @ternType fn(item: {data: string, userData: {}}) -> string
   * @ternDoc Finds the name of an item/note.  It is found from the title (# tag) at the start of the item.  If the item has no title 'Untitled' is returned.
   */
  getItemName (item) {
    console.log('getitemname')
    const maxNameLength = 128
    const defaultName = 'Untitled'
    if (!item || !item.data || !(typeof item.data === 'string')) return defaultName
    let possibleName = item.data.substr(0, maxNameLength).trim()
    if (possibleName.startsWith('# ')) {
      let newLineIndex = possibleName.indexOf('\n')
      return possibleName.substr(2, newLineIndex >= 0 ? newLineIndex - 1 : possibleName.length).trim()
    } else {
      return defaultName
    }
  }

  /**
   * Parses markdown to HTML string.
   * @param markdown
   * @public
   * @name parseMd
   * @ternType fn(markdown: string) -> string
   * @ternDoc Parses markdown to HTML string.
   */
  parseMd (markdown) {
    return marked(markdown)
  }

  /**
   * Parses the markdown and removes the <p></p> tags surrounding it.  Good for making text inline.
   * @param markdown
   * @public
   * @name parseMdInline
   * @ternType fn(markdown: string) -> string
   * @ternDoc Parses the markdown and removes the <p></p> tags surrounding it.  Good for making text inline.
   */
  parseMdInline (markdown) {
    return marked(markdown).trim().slice(3, -4)
  }

  /**
   * Generates a UUID
   * @param dashes  boolean if true result like 'abcd-1234-aabb', if false result like 'abcd1234aabb'
   * @param v1      boolean if false generates a v4 (random) uuid, if true generates a v1 (time based) uuid
   * @public
   * @name generateUUID
   * @ternType fn(dashes: boolean, v1: boolean) -> string
   * @ternDoc Generates a UUID.  If v1=false generates a v4 (random) uuid, if v1=true generates a v1 (time based) uuid.
   */
  generateUUID (dashes = false, v1 = false) {
    let v = ''
    if (v1) {
      v = uuid.v1()
    } else {
      v = uuid.v4()
    }
    if (!dashes) v = v.replace(/-/g, '')
    return v
  }

  _resetStorageURLs () {
    this._storageURLs = []
  }

  /**
   * Gets a storage URL
   * @param filename  string the filename of the resource
   * @param rawNote   {*} eg. noteHelper.note
   * @public
   * @name getStorageUrl
   * @ternType fn(filename: string, rawNote: {*}) -> string
   * @ternDoc Gets a storage URL from a filename.  Accounts for weather this is a public or private note.
   */
  getStorageUrl (filename, rawNote) {
    let path = ''
    if ((window.location.host).match('^' + (process.env.PUBLIC_URL || 'https://public.submind.io'))) {
      path = 'public/' + rawNote.publisher + '/' + rawNote.id + '/' + filename
    } else {
      path = 'users/' + this._store().state.auth.currentUser.uid + '/' + filename
    }
    let imgRef = firebase.storage().ref(path)
    let t = this
    // Get the download URL
    return imgRef.getDownloadURL().then((url) => {
      // Insert url into an <img> tag to "download"
      t._storageURLs.push({ filename, rawNote, url })
      return url
    })
  }
}

export default new ComponentHelper()
